import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { Empty } from '../../components/ui/Empty';

interface RemovalRequest {
  id: string;
  phone_number: string;
  notes: string | null;
  status: 'UNDER_REVIEW' | 'REMOVED' | 'REJECTED';
  requested_by: string | null;
  created_at: string;
}

export const AdminRemovalRequests = () => {
  const [requests, setRequests] = useState<RemovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('removal_requests' as any)
        .select('id, phone_number, notes, status, requested_by, created_at')
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setRequests((data as any) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load removal requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, phoneNumber: string, status: RemovalRequest['status']) => {
    try {
      if (status === 'REMOVED') {
        const { error: deleteError } = await supabase
          .from('phone_book' as any)
          .delete()
          .eq('phone_number', phoneNumber);
        if (deleteError) throw deleteError;
      }

      const { error: updateError } = await supabase
        .from('removal_requests' as any)
        .update({ status } as any)
        .eq('id', id);
      if (updateError) throw updateError;
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request');
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  if (loading) return <Loading />;
  if (error) return <ErrorAlert message={error} onRetry={loadRequests} />;
  if (requests.length === 0) return <Empty title="No removal requests" message="There are no number removal requests yet." />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Removal Requests</h1>
        <p className="text-sm text-gray-600">Number removal requests submitted by users.</p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Phone Number</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Notes</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Requested By</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {requests.map((req) => (
              <tr key={req.id}>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {new Date(req.created_at).toLocaleDateString('en-IN')}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{req.phone_number}</td>
                <td className="max-w-xs px-4 py-3 text-sm text-gray-700">{req.notes ?? '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{req.requested_by ?? '-'}</td>
                <td className="px-4 py-3 text-sm">
                  <select
                    value={req.status}
                    onChange={(e) => handleStatusChange(req.id, req.phone_number, e.target.value as RemovalRequest['status'])}
                    className="rounded border border-gray-300 px-2 py-1 text-xs"
                  >
                    <option value="UNDER_REVIEW">Under Review</option>
                    <option value="REMOVED">Removed</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
